// Ported from src/components/DeviceControls/DeviceControls.js on 2026-10-05.
import type {
  DeviceControlsProps,
  DeviceControlDraft,
  DeviceControlField,
} from 'features/Integrations/types/deviceControl.types';
import DeviceCommandFields from './components/DeviceCommandFields/DeviceCommandFields';
import DeviceAudioSelector from './components/DeviceAudioSelector/DeviceAudioSelector';
import BaseTooltip from 'components/Tooltips/BaseTooltip';
import LegacyTextToggle from 'components/UI/TextToggle';
import type { DeviceControlToggleComponent } from 'features/Integrations/types/deviceControl.types';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import Hls from 'hls.js';
import useSirenAudio from 'features/Integrations/hooks/useSirenAudio';
import { getChangedCommandFields } from 'features/Integrations/utils/devices.utils';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import apiClient from 'services/_infra/apiClient';

const TextToggle = LegacyTextToggle as DeviceControlToggleComponent;

/**
 * @param {{device: object, displayMessage?: {type: string, message: string}, on?: (event: string, data: object), sending?: boolean}}
 */
export default function DeviceControls({
  device,
  devices,
  displayMessage,
  audioOverride,
  on,
  sending = null,
  audioManagerButtonEnabled = true,
  stopLiveAudioSignal = null,
  compact = false,
  // Group mode: false when every siren already has its own file in the rows
  // below the controls, so an alert may go without a group-wide file.
  audioRequired = true,
  // True when Save sends a group command to every member (the group panel and modal), not a
  // command to the one device in `device` (the dashboard group widget).
  sendsGroupCommand = false,
  // The one device a single-device command reaches when `device` is a stand-in built from several
  // members (the dashboard group widget). The override rules read this device's own row.
  commandedDevice = null,
}: DeviceControlsProps) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('DeviceControls');
    }
  } catch (e) {}
  const { allAudio, universalAudio, sirenAudio, groupAudio } = useSirenAudio(devices || device);
  // A group may pick ANY file in the library: on Save the backend associates it
  // with every siren in the group that lacks it (Mike, 2026-08-28: "if we
  // select a file to send we should associate that file with all of them
  // automatically"). Files not yet on every siren say so in the list.
  const audioList = useMemo(
    () => (devices ? [...allAudio] : [...universalAudio, ...sirenAudio]),
    [devices, allAudio, universalAudio, sirenAudio],
  );
  const onEverySiren = useMemo(
    () => new Set<number>((groupAudio || []).map((a) => a?.id)),
    [groupAudio],
  );
  const perms = usePerms();
  // The override counts only while the operator may still see and change it: the same rule
  // that shows the checkbox. A box ticked before full maintenance began lifts no gate and is
  // not sent, because the backend bypasses the kill switch for it.
  // Full maintenance: the kill switch on AND the bridge's tamper input open (status -1). The
  // backend's override never fires such a device (Mike, 2026-10-12). A group command offers the
  // override while any member can still take it; the backend skips the members in full maintenance.
  const isInFullMaintenance = (target?: DeviceControlDraft | null) =>
    Boolean(target?.kill_switch) && !(target?.data?.[0]?.status > -1);
  // Locked: the kill switch is on or the tamper input is open (status below 0).
  const isLocked = (target?: DeviceControlDraft | null) =>
    Boolean(target?.kill_switch) || target?.data?.[0]?.status < 0;
  const overrideTarget = commandedDevice ?? device;
  const isOverrideAvailable =
    perms.isMaintenanceOperator() &&
    (sendsGroupCommand && devices?.length
      ? devices.some((member) => !isInFullMaintenance(member))
      : !isInFullMaintenance(overrideTarget));
  // The toggle and Save gates read the device the command reaches.
  const isTargetLocked = isLocked(overrideTarget);
  const isOverrideActive = isOverrideAvailable && !!device?.maintenanceOverride;
  // Several DeviceControls can share a page, so the caveat's id has to be per-instance.
  const instanceId = useId();
  const audioIdleCaveatId = `${instanceId}audio-idle-caveat`;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [type, setType] = useState('');
  const [changes, setChanges] = useState(false);
  const [removeMe, setRemoveMe] = useState(false);
  const [liveAudio, setLiveAudio] = useState(false);
  /**
   * Why Listen Live cannot play, when it cannot.
   *
   * The button used to flip to "playing" whatever happened, and every failure
   * went only to `console.error` — so a device with no camera looked exactly
   * like a device playing silence. A demo siren has no camera by design, so
   * that would have been its permanent state.
   *
   * The control stays VISIBLE either way. It just stops pretending.
   */
  const [liveAudioUnavailable, setLiveAudioUnavailable] = useState<string | null>(null);

  // Track changes only for user-initiated edits (avoid false "unsaved" on init).
  // The parent keeps only the fields named in editedFields when live data refreshes.
  const setData = (
    newState: DeviceControlDraft,
    trackChanges = true,
    isUserEdit = trackChanges,
  ) => {
    on('update', newState, {
      editedFields: isUserEdit ? getChangedCommandFields(device, newState) : [],
    });
    if (trackChanges) setChanges(true);
  };

  useEffect(() => {
    // Log device and context info on modal open
    // console.log('[DeviceControls] modal opened', {
    //   device,
    //   devices,
    //   deviceType: device?.device_type || '',
    // });

    on('open');

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      on('close');
    };
  }, []);

  useEffect(() => {
    if (stopLiveAudioSignal == null) return;
    stopLiveAudio();
  }, [stopLiveAudioSignal]);

  useEffect(() => {
    if (!device && !devices) return;
    // A temporary file comes from the operator's choice in the audio manager, so it is an edit.
    if (audioOverride)
      setData(
        {
          ...device,
          status: audioOverride?.relay_id || true,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: -1,
          audio: audioOverride,
        },
        false,
        true,
      );
    else
      setData(
        {
          ...device,
          status: false,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: null,
          audio: null,
        },
        false,
      );
  }, [audioOverride]);

  useEffect(() => {
    if (!device && !devices) return;
    setType(device?.device_type || '');

    let newDevice = { ...device };
    let changed = false;

    if (device?.status === undefined || device?.toggle_switch_text === undefined) {
      newDevice = {
        ...newDevice,
        toggle_switch_text: device?.data?.[0]?.toggle_switch ? 'On' : 'Off',
        status: device?.status || !!device?.data?.[0]?.status,
      };
      changed = true;
    }

    if (
      Number(device?.data?.[0]?.command) > 0 &&
      device?.device_type.includes('Siren') &&
      device?.audioIndex === undefined &&
      audioList
    ) {
      const audio = audioList.findIndex((audio) => audio.relay_id === device?.data?.[0].command);

      if (audio >= 0) {
        newDevice = {
          ...newDevice,
          audioIndex: audio,
          audio: audioList[audio],
        };
        changed = true;
      }
    }

    if (changed) setData(newDevice, false);
  }, [device, audioList]);

  const toggleSirenLiveAudio = async () => {
    if (!!devices) return;

    if (liveAudio) stopLiveAudio();
    else startLiveAudio();
  };

  const getLiveAudioDeviceId = () => {
    if (device?.id) {
      return device?.id;
    }

    return null;
  };

  /**
   * Why this device cannot serve live audio, or null when it can try.
   *
   * Listen Live is the AUDIO TRACK OF A PAIRED CAMERA, not something the siren
   * itself serves. With no camera the backend answers 404 and there is nothing
   * to play, so there is no reason to open a connection and wait to find out.
   *
   * A demo siren is named explicitly rather than lumped in with "no camera".
   * It may well be paired with a camera for the sake of the demonstration, and
   * it still must never open a stream. The word is DEMO, never "simulated" and
   * never against "real" (Mike, 2026-09-01): a deployment is all real or all
   * demo, so the line says what this device cannot do, not which fleet it is.
   */
  // Greyed out, not hidden: the control sits where an integrated siren's sits.
  const isDemoDevice = Number(device?.is_simulated) === 1;
  const demoLiveAudioMessage = 'Live audio is not available for this demo device.';

  const liveAudioBlockedReason = () => {
    if (isDemoDevice) return demoLiveAudioMessage;
    if (!device?.camera) return 'No camera is paired with this device, so there is no live audio.';
    return null;
  };

  const failLiveAudio = (reason: string, error: Error | import('hls.js').ErrorData) => {
    if (error) console.error('Error in DeviceControls:Audio:', error);
    stopLiveAudio();
    setLiveAudio(false);
    setLiveAudioUnavailable(reason);
  };

  const startLiveAudio = () => {
    const liveAudioDeviceId = getLiveAudioDeviceId();
    if (!liveAudioDeviceId || !audioRef.current) {
      return;
    }

    // Never open a connection we already know cannot succeed.
    const blocked = liveAudioBlockedReason();
    if (blocked) {
      setLiveAudioUnavailable(blocked);
      setLiveAudio(false);
      return;
    }

    const url = `${process.env.PUBLIC_API_URL}/v2/integrations/device/siren/${encodeURIComponent(liveAudioDeviceId)}/audio-stream/index.m3u8`;

    stopLiveAudio();
    setLiveAudioUnavailable(null);

    if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      audioRef.current.src = url;
      audioRef.current.play().catch((error) => {
        failLiveAudio('Live audio could not be started.', error);
      });
      setLiveAudio(true);
      return;
    }

    const hls = new Hls();
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(audioRef.current);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      audioRef.current?.play().catch((error) => {
        failLiveAudio('Live audio could not be started.', error);
      });
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Only a fatal error means the stream is gone. hls.js reports recoverable
      // ones constantly during normal playback, and treating those as failure
      // would make a working stream flicker "unavailable".
      if (data?.fatal) {
        failLiveAudio('The live audio stream is not available for this device.', data);
      } else {
        console.error('Error in DeviceControls:Hls:', data);
      }
    });
    setLiveAudio(true);
  };

  const stopLiveAudio = () => {
    hlsRef.current?.destroy();
    hlsRef.current = null;

    audioRef.current?.pause();
    audioRef.current?.removeAttribute('src');
    audioRef.current?.load();

    // Cleanup any lingering live audio stream
    const liveAudioDeviceId = getLiveAudioDeviceId();
    if (liveAudioDeviceId) {
      apiClient.delete(
        `/v2/integrations/device/siren/${encodeURIComponent(liveAudioDeviceId)}/audio-stream`,
      );
    }

    setLiveAudio(false);
  };

  const renderComponent = (component: DeviceControlField) => {
    if (component.type === 'AudioTextToggle') return renderSirenTextToggle(component);
    if (component.type === 'audio-dropdown')
      return (
        <DeviceAudioSelector
          device={device}
          devices={devices}
          audioOverride={audioOverride}
          on={on}
          audioManagerButtonEnabled={audioManagerButtonEnabled}
          compact={compact}
          audioList={audioList}
          onEverySiren={onEverySiren}
          audioIdleCaveatId={audioIdleCaveatId}
          canManageAudio={
            perms.testPermission([], ['CreateSirenAudioFiles']) ||
            perms.testPermission([], ['Edit-DeleteSirenAudioFiles'])
          }
          setData={setData}
          stopLiveAudio={stopLiveAudio}
        />
      );
    return (
      <DeviceCommandFields
        component={component}
        device={device}
        displayMessage={displayMessage}
        audioOverride={audioOverride}
        on={on}
        sending={sending}
        compact={compact}
        audioRequired={audioRequired}
        changes={changes}
        removeMe={removeMe}
        setRemoveMe={setRemoveMe}
        isOverrideAvailable={isOverrideAvailable}
        isOverrideActive={isOverrideActive}
        isTargetLocked={isTargetLocked}
        setData={setData}
      />
    );
  };

  const renderSirenTextToggle = (component: DeviceControlField) => {
    const liveAudioDeviceId = getLiveAudioDeviceId();
    let properties = {
      disabled: isTargetLocked && !isOverrideActive,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div
          className={
            compact
              ? 'd-flex flex-column align-items-stretch gap-2'
              : 'd-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-2 gap-lg-0'
          }
        >
          <div
            className={
              compact
                ? 'd-flex flex-column gap-2'
                : 'd-flex flex-column flex-lg-row flex-fill flex-basis-0 justify-content-between align-items-stretch align-items-lg-center'
            }
          >
            <div className="flex-fill flex-basis-0">
              <label>{component.label}</label>
            </div>
            <div className="flex-fill">
              <TextToggle {...properties} />
            </div>
          </div>

          <div
            className={`d-flex flex-wrap gap-2 ${
              compact ? 'justify-content-end' : 'flex-fill flex-basis-0 justify-content-lg-end'
            }`}
          >
            {/*
              The Listen Live control stays VISIBLE even when there is no feed,
              so the interface reads the same as an integrated siren. What it
              must not do is claim to be playing — see `liveAudioUnavailable`.
            */}
            {!devices &&
              liveAudioDeviceId &&
              (isDemoDevice ? (
                <BaseTooltip
                  id="disabled-listen-live-tooltip"
                  text="No VMS devices are linked to the selected Siren/Giant Voice devices"
                  placement="top"
                >
                  <span className="d-block">
                    <Button
                      variant="primary"
                      onClick={toggleSirenLiveAudio}
                      disabled={isDemoDevice}
                      aria-label={liveAudio ? 'Stop listening' : 'Listen in'}
                    >
                      {!liveAudio && <IoVolumeMute size={25} />}
                      {liveAudio && <IoVolumeHigh size={25} />}
                    </Button>
                  </span>
                </BaseTooltip>
              ) : (
                <Button
                  variant="primary"
                  onClick={toggleSirenLiveAudio}
                  disabled={isDemoDevice}
                  title={
                    liveAudioUnavailable
                      ? liveAudioUnavailable
                      : liveAudio
                        ? 'Stop listening'
                        : 'Listen in'
                  }
                  aria-label={liveAudio ? 'Stop listening' : 'Listen in'}
                >
                  {!liveAudio && <IoVolumeMute size={25} />}
                  {liveAudio && <IoVolumeHigh size={25} />}
                </Button>
              ))}

            {/* A stop command: a maintenance operator's override lets it reach a device in
                maintenance, full maintenance included (the backend checks the role). */}
            <Button
              variant="primary"
              onClick={() =>
                // Always from an operator: the page may not show a lock that is there now, and a
                // stop must not fail for that. The backend records the override only when used.
                on('cancel-audio', { maintenanceOverride: perms.isMaintenanceOperator() })
              }
            >
              Cancel Audio
            </Button>
          </div>
        </div>

        {liveAudioUnavailable && (
          <div className="text-muted small mt-1 text-lg-end">{liveAudioUnavailable}</div>
        )}

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  // Helper function for renderAudioDropdown. `-1` is the "no selection" sentinel, and
  // BaseSelect's clear also lands here as null.

  if (!device && !devices) return null;

  return (
    <div className={`container-fluid d-flex flex-column gap-2 ${compact ? 'px-0' : ''}`}>
      {(
        layout[type.toLowerCase() as keyof typeof layout]?.components as
          | DeviceControlField[]
          | undefined
      )?.map((component, index) => (
        <div key={`${component.type}-${index}`}>{renderComponent(component)}</div>
      ))}
      <audio className="hidden" ref={audioRef} autoPlay />
    </div>
  );
}
